import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { FileSpreadsheet, Plug, RefreshCcw, Trash2, Plus, X, Eye, Save, History, Pencil, Sparkles, Zap, AlertTriangle, CalendarClock } from "lucide-react";
import StatusBadge from "./StatusBadge";
import { formatDate } from "../utils/format";

const REDIRECT_URI_SUGGEST = `${process.env.REACT_APP_BACKEND_URL || ""}/api/auth/google-sheets/callback`;
const LEAD_FIELDS = ["name", "phone", "city", "problem", "date", "status", "page_url", "page_title", "campaign_name", "source"];

export default function GoogleSheetsConnectorPanel() {
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [clients, setClients] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [lps, setLps] = useState([]);

  // Builder + editor modal state
  const [builder, setBuilder] = useState(null);
  const [editor, setEditor] = useState(null);  // mapping object to edit
  const [logsFor, setLogsFor] = useState(null);
  const [logs, setLogs] = useState([]);
  const [testResult, setTestResult] = useState(null);  // { mapping, data }

  const loadAll = async () => {
    const [c, s, p, m, cl, camp, lp] = await Promise.all([
      api.get("/admin/connectors/google-sheets/config"),
      api.get("/connectors/google-sheets/status"),
      api.get("/google-sheets/profiles").catch(() => ({ data: [] })),
      api.get("/google-sheets/mappings").catch(() => ({ data: [] })),
      api.get("/clients"),
      api.get("/campaigns"),
      api.get("/landing-pages"),
    ]);
    setConfig(c.data);
    setStatus(s.data);
    setProfiles(p.data);
    setMappings(m.data);
    setClients(cl.data);
    setCampaigns(camp.data);
    setLps(lp.data);
  };

  useEffect(() => { loadAll(); }, []);

  // OAuth postMessage listener
  useEffect(() => {
    const h = (e) => {
      if (e.data?.type === "gsheets_oauth") {
        if (e.data.ok) toast.success("Google Sheets connected"); else toast.error("OAuth failed");
        loadAll();
      }
    };
    window.addEventListener("message", h);
    return () => window.removeEventListener("message", h);
  }, []);

  const saveConfig = async () => {
    try {
      await api.put("/admin/connectors/google-sheets/config", form);
      toast.success("Config saved");
      setForm(null);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const connect = async () => {
    try {
      const { data } = await api.get("/auth/google-sheets/start");
      const w = window.open(data.authorize_url, "gsheets_oauth", "width=520,height=680");
      if (!w) toast.error("Popup blocked — allow popups for this site");
    } catch (e) { toast.error(e.response?.data?.detail || "OAuth start failed"); }
  };

  const deleteProfile = async (id) => {
    if (!window.confirm("Disconnect this Google account? Mappings will be paused.")) return;
    await api.delete(`/google-sheets/profiles/${id}`);
    toast.success("Disconnected");
    loadAll();
  };

  const syncMapping = async (id, force = false) => {
    const tid = toast.loading(force ? "Force re-syncing…" : "Syncing…");
    try {
      const { data } = await api.post(
        `/google-sheets/mappings/${id}/sync-now${force ? "?force_reprocess=true" : ""}`
      );
      const reasons = data.skipped_reasons || {};
      const reasonStr = Object.entries(reasons).map(([k, v]) => `${k}:${v}`).join(" · ");
      if (data.total_rows > 0 && data.inserted === 0) {
        toast.error(
          `Sync warning · 0 of ${data.total_rows} rows imported. Reasons: ${reasonStr || "none reported"}. Open Logs ▸ for details.`,
          { id: tid, duration: 9000 }
        );
      } else if (data.warning_level === "partial_skip") {
        toast.success(
          `Sync done · ${data.inserted} new · ${data.skipped} skipped (${reasonStr})`,
          { id: tid, duration: 6000 }
        );
      } else {
        toast.success(
          `Sync done · ${data.inserted} new leads, ${data.skipped} skipped`,
          { id: tid }
        );
      }
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed", { id: tid, duration: 8000 });
    }
  };

  const testMapping = async (m) => {
    const tid = toast.loading("Running test (no writes)…");
    try {
      const { data } = await api.post(`/google-sheets/mappings/${m.id}/test?preview_rows=5`);
      toast.dismiss(tid);
      setTestResult({ mapping: m, data });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test failed", { id: tid });
    }
  };

  const cleanupHeaders = async (m) => {
    const tid = toast.loading("Scanning for header-row leads…");
    try {
      const { data } = await api.post(`/google-sheets/mappings/${m.id}/cleanup-headers?dry_run=true`);
      toast.dismiss(tid);
      const count = data.match_count || 0;
      if (count === 0) {
        toast.success("No header-row leads detected — nothing to clean.");
        return;
      }
      const sample = data.matches.slice(0, 3).map((x) => `• ${x.name || "—"} / ${x.phone || "—"} / ${x.city || "—"}`).join("\n");
      const ok = window.confirm(
        `Found ${count} suspected header-row lead${count > 1 ? "s" : ""} for this mapping:\n\n${sample}${count > 3 ? `\n…and ${count - 3} more` : ""}\n\nDelete them now? (cannot be undone)`
      );
      if (!ok) return;
      const tid2 = toast.loading(`Deleting ${count}…`);
      const r2 = await api.post(`/google-sheets/mappings/${m.id}/cleanup-headers?dry_run=false`);
      toast.success(`Removed ${r2.data.deleted} header-row leads`, { id: tid2 });
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cleanup failed", { id: tid });
    }
  };

  const reprocessDates = async (m) => {
    if (!window.confirm("Re-fetch the live sheet and update existing leads' dates from the actual sheet cell? This will not insert or delete any leads.")) return;
    const tid = toast.loading("Reprocessing dates…");
    try {
      const { data } = await api.post(`/google-sheets/mappings/${m.id}/reprocess-dates`);
      toast.success(
        `Reprocess done · ${data.updated} dates updated · ${data.unchanged} unchanged · ${data.cleared} cleared (now unparseable)`,
        { id: tid, duration: 8000 }
      );
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reprocess failed", { id: tid, duration: 8000 });
    }
  };

  const toggleActive = async (m) => {
    await api.patch(`/google-sheets/mappings/${m.id}`, { active: !m.active });
    loadAll();
  };

  const deleteMapping = async (id) => {
    if (!window.confirm("Delete this mapping? Lead data already imported is kept.")) return;
    await api.delete(`/google-sheets/mappings/${id}`);
    toast.success("Mapping deleted");
    loadAll();
  };

  const openLogs = async (m) => {
    setLogsFor(m);
    const { data } = await api.get(`/google-sheets/mappings/${m.id}/logs`);
    setLogs(data);
  };

  return (
    <div className="space-y-6" data-testid="gsheets-connector-panel">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-mono">Google Sheets</div>
          <div className="font-heading text-lg tracking-tight">Lead ingestion from Google Sheets</div>
          <p className="text-xs text-muted-foreground mt-1">
            Set OAuth creds, connect a Google account, then map sheet columns to lead fields. Sync runs every 15 minutes automatically.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge tone={status?.configured ? "success" : "muted"}>{status?.mode?.toUpperCase() || "MOCK"}</StatusBadge>
          <button onClick={loadAll} className="h-9 w-9 border border-border rounded-sm flex items-center justify-center"><RefreshCcw className="w-3.5 h-3.5" /></button>
        </div>
      </div>

      {/* Config */}
      <div className="bg-card border border-border rounded-sm p-5">
        <div className="label-mono mb-3">OAuth configuration</div>
        {config?.configured && !form ? (
          <div className="space-y-1 text-sm">
            <Row k="Client ID" v={<span className="font-mono text-xs">{config.client_id}</span>} />
            <Row k="Client Secret" v={<span className="font-mono text-xs">{config.client_secret_masked}</span>} />
            <Row k="Redirect URI" v={<span className="font-mono text-xs break-all">{config.redirect_uri}</span>} />
            <Row k="Mode" v={<StatusBadge tone={config.mode === "live" ? "success" : "muted"}>{config.mode}</StatusBadge>} />
            <button onClick={() => setForm({
              client_id: config.client_id, client_secret: "", redirect_uri: config.redirect_uri, mode: config.mode,
            })} className="mt-3 text-xs text-primary hover:underline" data-testid="gsheets-edit-config">Edit configuration</button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Redirect URI suggestion (add this to your OAuth client in Google Cloud Console):{" "}
              <span className="font-mono break-all">{REDIRECT_URI_SUGGEST}</span>
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <input placeholder="Client ID" value={form?.client_id || ""} onChange={(e) => setForm({ ...(form || {}), client_id: e.target.value })} className="h-10 px-3 border border-border rounded-sm text-sm font-mono" data-testid="gsheets-client-id" />
              <input placeholder="Client Secret" type="password" value={form?.client_secret || ""} onChange={(e) => setForm({ ...(form || {}), client_secret: e.target.value })} className="h-10 px-3 border border-border rounded-sm text-sm font-mono" data-testid="gsheets-client-secret" />
              <input placeholder="Redirect URI" value={form?.redirect_uri || ""} onChange={(e) => setForm({ ...(form || {}), redirect_uri: e.target.value })} className="h-10 px-3 border border-border rounded-sm text-sm font-mono md:col-span-2" data-testid="gsheets-redirect-uri" />
              <select value={form?.mode || "live"} onChange={(e) => setForm({ ...(form || {}), mode: e.target.value })} className="h-10 px-3 border border-border rounded-sm text-sm md:col-span-2">
                <option value="live">LIVE</option><option value="mock">MOCK</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button onClick={saveConfig} data-testid="gsheets-save-config" className="h-10 px-4 bg-primary text-primary-foreground text-sm font-medium rounded-sm">Save</button>
              {config?.configured && <button onClick={() => setForm(null)} className="h-10 px-4 border border-border text-sm rounded-sm">Cancel</button>}
            </div>
          </div>
        )}
      </div>

      {/* Connected profiles */}
      {config?.configured && config.mode === "live" && (
        <div className="bg-card border border-border rounded-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="label-mono">Connected Google accounts ({profiles.length})</div>
            <button onClick={connect} data-testid="gsheets-connect-btn" className="h-9 px-3 bg-primary text-primary-foreground text-xs font-medium rounded-sm flex items-center gap-1.5"><Plug className="w-3.5 h-3.5" /> Connect Google Account</button>
          </div>
          {profiles.length === 0 && <div className="text-xs text-muted-foreground">No accounts connected yet.</div>}
          {profiles.map((p) => (
            <div key={p.id} className="py-3 border-b border-border last:border-b-0 flex justify-between items-center">
              <div>
                <div className="text-sm font-medium">{p.connected_google_email || "—"}</div>
                <div className="text-[11px] text-muted-foreground font-mono">Added {formatDate(p.created_at)}</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setBuilder({ profile_id: p.id, step: 1 })} data-testid={`gsheets-add-mapping-${p.id}`} className="h-8 px-3 bg-primary text-primary-foreground text-xs font-medium rounded-sm flex items-center gap-1"><Plus className="w-3 h-3" /> Add mapping</button>
                <button onClick={() => deleteProfile(p.id)} className="h-8 px-3 border border-[hsl(var(--critical))]/30 text-[hsl(var(--critical))] text-xs rounded-sm flex items-center gap-1"><Trash2 className="w-3 h-3" /> Disconnect</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Mappings */}
      {mappings.length > 0 && (
        <div className="bg-card border border-border rounded-sm overflow-hidden" data-testid="gsheets-mappings-table">
          <div className="px-5 py-3 border-b border-border flex justify-between">
            <div className="label-mono">Active mappings ({mappings.filter((m) => m.active).length} of {mappings.length})</div>
            <button onClick={async () => { await api.post("/google-sheets/sync-all-now"); toast.success("All active mappings synced"); loadAll(); }} data-testid="gsheets-sync-all-btn" className="text-xs text-primary hover:underline">Sync all now →</button>
          </div>
          <table className="w-full data-table">
            <thead><tr><th>Sheet</th><th>Tab</th><th>Client / Campaign</th><th>Last sync</th><th>Rows → Leads</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {mappings.map((m) => (
                <tr key={m.id}>
                  <td><div className="text-sm font-medium">{m.spreadsheet_name}</div><div className="text-[11px] text-muted-foreground font-mono">{m.spreadsheet_id.slice(0, 16)}…</div></td>
                  <td className="font-mono text-xs">{m.sheet_name}</td>
                  <td className="text-xs text-muted-foreground">{clients.find((c) => c.id === m.client_id)?.name || "—"} / {campaigns.find((c) => c.id === m.campaign_id)?.name || "—"}</td>
                  <td className="text-xs">{m.last_sync_at ? formatDate(m.last_sync_at) : "never"}</td>
                  <td className="tabular text-xs">{m.last_row_count || 0} → {m.total_rows_inserted || 0}</td>
                  <td>
                    {m.last_error
                      ? <StatusBadge tone="critical" title={m.last_error}>error</StatusBadge>
                      : <StatusBadge tone={m.active ? "success" : "muted"}>{m.active ? "active" : "paused"}</StatusBadge>}
                  </td>
                  <td>
                    <div className="flex gap-1 flex-wrap">
                      <button onClick={() => syncMapping(m.id)} data-testid={`gsheets-sync-${m.id}`} className="px-2 py-1 text-[11px] border border-border rounded-sm hover:bg-secondary" title="Sync new rows"><RefreshCcw className="w-3 h-3" /></button>
                      <button onClick={() => testMapping(m)} data-testid={`gsheets-test-${m.id}`} className="px-2 py-1 text-[11px] border border-border rounded-sm hover:bg-secondary" title="Test (preview, no writes)"><Sparkles className="w-3 h-3" /></button>
                      <button onClick={() => { if (window.confirm("Force re-sync? This will re-evaluate every row in the sheet (still de-duped by row hash, no duplicates created).")) syncMapping(m.id, true); }} data-testid={`gsheets-force-${m.id}`} className="px-2 py-1 text-[11px] border border-border rounded-sm hover:bg-secondary" title="Force re-sync all rows"><Zap className="w-3 h-3" /></button>
                      <button onClick={() => reprocessDates(m)} data-testid={`gsheets-reprocess-${m.id}`} className="px-2 py-1 text-[11px] border border-border rounded-sm hover:bg-secondary" title="Reprocess dates (refresh existing leads' lead_date from sheet)"><CalendarClock className="w-3 h-3" /></button>
                      <button onClick={() => cleanupHeaders(m)} data-testid={`gsheets-cleanup-${m.id}`} className="px-2 py-1 text-[11px] border border-[hsl(38_92%_45%)]/40 text-[hsl(38_92%_30%)] rounded-sm hover:bg-[hsl(38_92%_50%)]/10" title="Cleanup header-row leads (e.g. name='guardian-name')"><AlertTriangle className="w-3 h-3" /></button>
                      <button onClick={() => setEditor({ ...m })} data-testid={`gsheets-edit-${m.id}`} className="px-2 py-1 text-[11px] border border-border rounded-sm hover:bg-secondary" title="Edit mapping"><Pencil className="w-3 h-3" /></button>
                      <button onClick={() => openLogs(m)} data-testid={`gsheets-logs-${m.id}`} className="px-2 py-1 text-[11px] border border-border rounded-sm hover:bg-secondary" title="Sync logs"><History className="w-3 h-3" /></button>
                      <button onClick={() => toggleActive(m)} className="px-2 py-1 text-[11px] border border-border rounded-sm hover:bg-secondary" title={m.active ? "Pause" : "Resume"}>{m.active ? "⏸" : "▶"}</button>
                      <button onClick={() => deleteMapping(m.id)} className="px-2 py-1 text-[11px] border border-[hsl(var(--critical))]/30 text-[hsl(var(--critical))] rounded-sm" title="Delete"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Builder modal */}
      {builder && (
        <MappingBuilder
          builder={builder}
          setBuilder={setBuilder}
          clients={clients} campaigns={campaigns} lps={lps}
          onDone={() => { setBuilder(null); loadAll(); }}
        />
      )}

      {/* Editor modal */}
      {editor && (
        <EditMappingModal
          mapping={editor}
          clients={clients} campaigns={campaigns} lps={lps}
          onClose={() => setEditor(null)}
          onSaved={() => { setEditor(null); loadAll(); }}
        />
      )}

      {/* Logs modal */}
      {logsFor && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setLogsFor(null)}>
          <div className="bg-card border border-border rounded-sm p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="gsheets-logs-modal">
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="font-heading text-lg font-medium">Sync logs</div>
                <div className="text-xs text-muted-foreground mt-1 font-mono">{logsFor.spreadsheet_name} · {logsFor.sheet_name}</div>
              </div>
              <button onClick={() => setLogsFor(null)}><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-2">
              {logs.length === 0 && <div className="text-xs text-muted-foreground">No logs yet — run sync now to create the first entry.</div>}
              {logs.map((l) => {
                const warn = l.warning_level === "no_rows_imported";
                const partial = l.warning_level === "partial_skip";
                const tone = !l.ok ? "critical" : warn ? "warning" : partial ? "warning" : "success";
                const palette = {
                  success: "border-[hsl(var(--success))]",
                  warning: "border-[hsl(38_92%_50%)] bg-[hsl(38_92%_50%)]/5",
                  critical: "border-[hsl(var(--critical))]",
                };
                return (
                  <div key={l.id} className={`border-l-2 pl-3 py-2 text-xs ${palette[tone]}`}>
                    <div className="flex justify-between">
                      <span className="font-semibold uppercase tracking-wider">
                        {!l.ok ? "Failed" : warn ? "Warning · 0 imported" : partial ? "Partial" : "Success"}
                      </span>
                      <span className="text-muted-foreground">{formatDate(l.at)}</span>
                    </div>
                    {l.ok ? (
                      <div className="text-muted-foreground mt-1">
                        {l.total_rows} rows · <span className="text-[hsl(var(--success))] font-semibold">{l.inserted} inserted</span> · {l.skipped} skipped · {l.errors || 0} errors
                      </div>
                    ) : (
                      <div className="text-[hsl(var(--critical))] mt-1 break-all">{l.error}</div>
                    )}
                    {l.skipped_reasons && Object.keys(l.skipped_reasons).length > 0 && (
                      <div className="mt-2 pt-2 border-t border-border/50">
                        <div className="font-semibold text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Skip reasons</div>
                        <div className="flex flex-wrap gap-1.5">
                          {Object.entries(l.skipped_reasons).map(([k, v]) => (
                            <span key={k} className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-sm bg-secondary font-mono">
                              {k}: <span className="font-bold">{v}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {l.warnings && Object.keys(l.warnings).length > 0 && (
                      <div className="mt-2">
                        <div className="font-semibold text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Warnings</div>
                        <div className="flex flex-wrap gap-1.5">
                          {Object.entries(l.warnings).map(([k, v]) => (
                            <span key={k} className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-sm bg-[hsl(38_92%_50%)]/10 text-[hsl(38_92%_30%)] font-mono">
                              {k}: <span className="font-bold">{v}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {l.detected_headers && warn && (
                      <details className="mt-2">
                        <summary className="text-[11px] text-muted-foreground cursor-pointer hover:text-foreground">Detected headers</summary>
                        <div className="font-mono text-[11px] mt-1 break-all bg-secondary/30 p-2 rounded">{(l.detected_headers || []).join(" · ")}</div>
                      </details>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Test (dry-run) result modal */}
      {testResult && (
        <TestResultModal result={testResult} onClose={() => setTestResult(null)} />
      )}
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between py-1.5 border-b border-border last:border-b-0"><span className="text-muted-foreground">{k}</span><span>{v}</span></div>;
}

function MappingBuilder({ builder, setBuilder, clients, campaigns, lps, onDone }) {
  const [sheets, setSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [columnMap, setColumnMap] = useState({});
  const [settings, setSettings] = useState({ client_id: "", campaign_id: "", landing_page_id: "", default_status: "new", default_relevant: true });

  useEffect(() => {
    if (builder.step === 1) {
      setLoading(true);
      api.get(`/google-sheets/profiles/${builder.profile_id}/spreadsheets`)
        .then((r) => setSheets(r.data))
        .catch((e) => toast.error(e.response?.data?.detail || "Failed to list spreadsheets"))
        .finally(() => setLoading(false));
    }
  }, [builder.step, builder.profile_id]);

  const pickSheet = async (ss) => {
    setSelectedSheet(ss);
    setLoading(true);
    try {
      const { data } = await api.post(`/google-sheets/profiles/${builder.profile_id}/preview`, { spreadsheet_id: ss.id });
      setPreview(data);
      // Auto-detect common mappings
      const auto = {};
      const headers = (data.headers || []).map((h) => h.toLowerCase().trim());
      LEAD_FIELDS.forEach((f) => {
        const idx = headers.findIndex((h) => h === f || h.includes(f));
        if (idx >= 0) auto[f] = data.headers[idx];
      });
      setColumnMap(auto);
      setBuilder({ ...builder, step: 2 });
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
    setLoading(false);
  };

  const save = async () => {
    if (!columnMap.phone) return toast.error("Please map at least the Phone column");
    const payload = {
      profile_id: builder.profile_id,
      spreadsheet_id: selectedSheet.id,
      spreadsheet_name: selectedSheet.name,
      sheet_name: preview.selected_sheet,
      column_map: Object.fromEntries(Object.entries(columnMap).filter(([_, v]) => v)),
      ...Object.fromEntries(Object.entries(settings).filter(([_, v]) => v !== "" && v !== null)),
      header_row: 1,
      active: true,
    };
    try {
      await api.post("/google-sheets/mappings", payload);
      toast.success("Mapping created. Click Sync now to import rows.");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setBuilder(null)}>
      <div className="bg-card border border-border rounded-sm p-6 max-w-3xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="gsheets-builder-modal">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="font-heading text-lg font-medium flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-primary" /> New sheet mapping</div>
            <div className="text-xs text-muted-foreground mt-1">Step {builder.step} of 2</div>
          </div>
          <button onClick={() => setBuilder(null)}><X className="w-4 h-4" /></button>
        </div>

        {builder.step === 1 && (
          <div>
            <div className="label-mono mb-2">Pick a spreadsheet</div>
            {loading && <div className="text-sm text-muted-foreground">Loading spreadsheets…</div>}
            <div className="max-h-80 overflow-y-auto border border-border rounded-sm">
              {sheets.map((s) => (
                <button key={s.id} onClick={() => pickSheet(s)} data-testid={`gsheets-pick-${s.id}`} className="w-full text-left px-3 py-2 border-b border-border last:border-b-0 hover:bg-secondary/30 flex justify-between items-center">
                  <span className="text-sm font-medium">{s.name}</span>
                  <span className="text-[11px] text-muted-foreground">{s.owner} · {formatDate(s.modified)}</span>
                </button>
              ))}
              {!loading && sheets.length === 0 && <div className="p-4 text-xs text-muted-foreground">No Google Sheets found in this account.</div>}
            </div>
          </div>
        )}

        {builder.step === 2 && preview && (
          <div className="space-y-4">
            <div>
              <div className="label-mono">Sheet</div>
              <div className="text-sm font-medium">{preview.title} · <span className="font-mono text-xs">{preview.selected_sheet}</span></div>
              {preview.sheets.length > 1 && (
                <select value={preview.selected_sheet} onChange={async (e) => {
                  const { data } = await api.post(`/google-sheets/profiles/${builder.profile_id}/preview`, { spreadsheet_id: selectedSheet.id, sheet_name: e.target.value });
                  setPreview(data);
                }} className="mt-2 h-9 px-2 border border-border rounded-sm text-sm">
                  {preview.sheets.map((s) => <option key={s.sheet_id} value={s.title}>{s.title} ({s.row_count} rows)</option>)}
                </select>
              )}
            </div>

            <div>
              <div className="label-mono mb-2">Header preview (first 5 data rows)</div>
              <div className="border border-border rounded-sm overflow-x-auto max-h-48">
                <table className="w-full data-table">
                  <thead><tr>{preview.headers.map((h, i) => <th key={i} className="whitespace-nowrap text-[11px]">{h}</th>)}</tr></thead>
                  <tbody>
                    {preview.sample_rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j} className="text-[11px] font-mono">{c}</td>)}</tr>)}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <div className="label-mono mb-2">Column map (Phone is required)</div>
              <div className="grid grid-cols-2 gap-2">
                {LEAD_FIELDS.map((f) => (
                  <div key={f}>
                    <div className="label-mono mb-1">{f}{f === "phone" && " *"}</div>
                    <select value={columnMap[f] || ""} onChange={(e) => setColumnMap({ ...columnMap, [f]: e.target.value })} data-testid={`gsheets-col-${f}`} className="w-full h-9 px-2 border border-border rounded-sm text-xs">
                      <option value="">— skip —</option>
                      {preview.headers.map((h, i) => <option key={i} value={h}>{h}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="label-mono mb-2">Defaults for imported leads</div>
              <div className="grid grid-cols-2 gap-2">
                <select value={settings.client_id} onChange={(e) => setSettings({ ...settings, client_id: e.target.value })} className="h-9 px-2 border border-border rounded-sm text-sm">
                  <option value="">No default client</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <select value={settings.campaign_id} onChange={(e) => setSettings({ ...settings, campaign_id: e.target.value })} className="h-9 px-2 border border-border rounded-sm text-sm">
                  <option value="">No default campaign</option>
                  {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <select value={settings.landing_page_id} onChange={(e) => setSettings({ ...settings, landing_page_id: e.target.value })} className="h-9 px-2 border border-border rounded-sm text-sm">
                  <option value="">No default landing page</option>
                  {lps.map((l) => <option key={l.id} value={l.id}>{l.title}</option>)}
                </select>
                <input value={settings.default_status} onChange={(e) => setSettings({ ...settings, default_status: e.target.value })} placeholder="Default status key (e.g. new)" className="h-9 px-2 border border-border rounded-sm text-sm" />
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <button onClick={() => setBuilder({ ...builder, step: 1 })} className="h-10 px-4 border border-border text-sm rounded-sm">← Back</button>
              <button onClick={save} data-testid="gsheets-save-mapping" className="flex-1 h-10 bg-primary text-primary-foreground text-sm font-medium rounded-sm flex items-center justify-center gap-1.5"><Save className="w-3.5 h-3.5" /> Save mapping</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}



function EditMappingModal({ mapping, clients, campaigns, lps, onClose, onSaved }) {
  const [draft, setDraft] = useState({
    client_id: mapping.client_id || "",
    campaign_id: mapping.campaign_id || "",
    landing_page_id: mapping.landing_page_id || "",
    sheet_name: mapping.sheet_name || "",
    spreadsheet_name: mapping.spreadsheet_name || "",
    default_status: mapping.default_status || "new",
    default_relevant: mapping.default_relevant !== false,
    active: mapping.active !== false,
  });
  const [columnMap, setColumnMap] = useState({ ...(mapping.column_map || {}) });
  // Optional multi-select state — seeded from existing arrays OR the
  // single-id field for back-compat.
  const seedIds = (single, list) =>
    list && list.length ? [...list] : single ? [single] : [];
  const [campaignIds, setCampaignIds] = useState(
    seedIds(mapping.campaign_id, mapping.campaign_ids)
  );
  const [landingPageIds, setLandingPageIds] = useState(
    seedIds(mapping.landing_page_id, mapping.landing_page_ids)
  );
  const [multiMode, setMultiMode] = useState(
    (mapping.campaign_ids?.length || 0) > 1 ||
    (mapping.landing_page_ids?.length || 0) > 1
  );
  const [saving, setSaving] = useState(false);

  // Filter campaigns + LPs by selected client (fixes the bug where the
  // landing-page dropdown was empty regardless of client choice).
  const filteredCampaigns = draft.client_id
    ? campaigns.filter((c) => c.client_id === draft.client_id)
    : campaigns;
  const filteredLps = draft.client_id
    ? lps.filter((l) => !l.client_id || l.client_id === draft.client_id)
    : lps;

  const setField = (k, v) => setDraft({ ...draft, [k]: v });

  const toggleId = (list, setter, id) => {
    setter(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (multiMode) {
        // Sync the *_ids arrays + null out the legacy single fields if
        // they're not in the array (so the mapping cleanly reflects the
        // multi-select state).
        payload.campaign_ids = campaignIds;
        payload.landing_page_ids = landingPageIds;
        payload.campaign_id = campaignIds[0] || null;
        payload.landing_page_id = landingPageIds[0] || null;
      } else {
        if (draft.client_id !== (mapping.client_id || "")) payload.client_id = draft.client_id || null;
        if (draft.campaign_id !== (mapping.campaign_id || "")) payload.campaign_id = draft.campaign_id || null;
        if (draft.landing_page_id !== (mapping.landing_page_id || "")) payload.landing_page_id = draft.landing_page_id || null;
        // Keep legacy & arrays consistent
        payload.campaign_ids = draft.campaign_id ? [draft.campaign_id] : [];
        payload.landing_page_ids = draft.landing_page_id ? [draft.landing_page_id] : [];
      }
      if (draft.client_id !== (mapping.client_id || "")) payload.client_id = draft.client_id || null;
      if (draft.sheet_name !== mapping.sheet_name) payload.sheet_name = draft.sheet_name;
      if (draft.spreadsheet_name !== mapping.spreadsheet_name) payload.spreadsheet_name = draft.spreadsheet_name;
      if (draft.default_status !== mapping.default_status) payload.default_status = draft.default_status;
      if (draft.default_relevant !== mapping.default_relevant) payload.default_relevant = draft.default_relevant;
      if (draft.active !== mapping.active) payload.active = draft.active;
      if (JSON.stringify(columnMap) !== JSON.stringify(mapping.column_map || {})) payload.column_map = columnMap;

      if (Object.keys(payload).length === 0) {
        toast.message("No changes");
        setSaving(false);
        return;
      }
      await api.patch(`/google-sheets/mappings/${mapping.id}`, payload);
      toast.success("Mapping updated");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const updateCol = (field, value) => setColumnMap({ ...columnMap, [field]: value });
  const removeCol = (field) => {
    const { [field]: _drop, ...rest } = columnMap;
    setColumnMap(rest);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-sm p-6 max-w-2xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="gsheets-edit-modal">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="font-heading text-lg font-medium flex items-center gap-2"><Pencil className="w-4 h-4 text-primary" /> Edit mapping</div>
            <div className="text-xs text-muted-foreground mt-1 font-mono break-all">{mapping.spreadsheet_id}</div>
          </div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>

        <div className="space-y-4">
          <EditField label="Spreadsheet name (display)">
            <input
              data-testid="gsheets-edit-spreadsheet-name"
              value={draft.spreadsheet_name}
              onChange={(e) => setField("spreadsheet_name", e.target.value)}
              className="w-full h-9 px-3 border border-border rounded-sm text-sm"
            />
            <div className="text-[11px] text-muted-foreground mt-1">
              To re-bind this mapping to a different spreadsheet, delete and recreate. Sync history is preserved for the existing ID.
            </div>
          </EditField>

          <EditField label="Tab / sheet name">
            <input
              data-testid="gsheets-edit-sheet-name"
              value={draft.sheet_name}
              onChange={(e) => setField("sheet_name", e.target.value)}
              className="w-full h-9 px-3 border border-border rounded-sm text-sm font-mono"
              placeholder="Sheet1"
            />
          </EditField>

          <EditField label="Mapped client">
            <select
              data-testid="gsheets-edit-client"
              value={draft.client_id}
              onChange={(e) => setField("client_id", e.target.value)}
              className="w-full h-9 px-2 border border-border rounded-sm text-sm"
            >
              <option value="">— None —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </EditField>

          <div className="bg-secondary/20 border border-border rounded-sm p-3">
            <label className="flex items-center gap-2 text-sm mb-3" data-testid="gsheets-edit-multi-toggle">
              <input
                type="checkbox"
                checked={multiMode}
                onChange={(e) => setMultiMode(e.target.checked)}
              />
              <span className="font-medium">Multi-select campaigns / landing pages</span>
              <span className="text-[11px] text-muted-foreground">(use when a single tab feeds multiple)</span>
            </label>

            {!multiMode ? (
              <div className="grid grid-cols-2 gap-3">
                <EditField label="Mapped campaign">
                  <select
                    data-testid="gsheets-edit-campaign"
                    value={draft.campaign_id}
                    onChange={(e) => setField("campaign_id", e.target.value)}
                    className="w-full h-9 px-2 border border-border rounded-sm text-sm"
                  >
                    <option value="">— None —</option>
                    {filteredCampaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </EditField>
                <EditField label="Landing page">
                  <select
                    data-testid="gsheets-edit-lp"
                    value={draft.landing_page_id}
                    onChange={(e) => setField("landing_page_id", e.target.value)}
                    className="w-full h-9 px-2 border border-border rounded-sm text-sm"
                  >
                    <option value="">— None —</option>
                    {filteredLps.map((p) => <option key={p.id} value={p.id}>{p.title || p.url}</option>)}
                  </select>
                </EditField>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <EditField label={`Campaigns (${campaignIds.length})`}>
                  <div className="border border-border rounded-sm max-h-40 overflow-y-auto" data-testid="gsheets-edit-campaign-multi">
                    {filteredCampaigns.length === 0 && <div className="p-2 text-[11px] text-muted-foreground">No campaigns for this client.</div>}
                    {filteredCampaigns.map((c) => (
                      <label key={c.id} className="flex items-center gap-2 px-2 py-1 text-xs hover:bg-secondary cursor-pointer">
                        <input
                          type="checkbox"
                          checked={campaignIds.includes(c.id)}
                          onChange={() => toggleId(campaignIds, setCampaignIds, c.id)}
                        />
                        <span>{c.name}</span>
                      </label>
                    ))}
                  </div>
                </EditField>
                <EditField label={`Landing pages (${landingPageIds.length})`}>
                  <div className="border border-border rounded-sm max-h-40 overflow-y-auto" data-testid="gsheets-edit-lp-multi">
                    {filteredLps.length === 0 && <div className="p-2 text-[11px] text-muted-foreground">No landing pages for this client.</div>}
                    {filteredLps.map((p) => (
                      <label key={p.id} className="flex items-center gap-2 px-2 py-1 text-xs hover:bg-secondary cursor-pointer">
                        <input
                          type="checkbox"
                          checked={landingPageIds.includes(p.id)}
                          onChange={() => toggleId(landingPageIds, setLandingPageIds, p.id)}
                        />
                        <span className="truncate">{p.title || p.url}</span>
                      </label>
                    ))}
                  </div>
                </EditField>
              </div>
            )}
          </div>

          <EditField label="Default lead status">
            <input
              value={draft.default_status}
              onChange={(e) => setField("default_status", e.target.value)}
              className="w-full h-9 px-2 border border-border rounded-sm text-sm"
              placeholder="new"
            />
          </EditField>

          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={draft.active} onChange={(e) => setField("active", e.target.checked)} data-testid="gsheets-edit-active" />
              Active (auto-syncs every 15 min)
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={draft.default_relevant} onChange={(e) => setField("default_relevant", e.target.checked)} />
              Mark new rows relevant by default
            </label>
          </div>

          <div>
            <div className="label-mono mb-2">Column mapping (lead field → sheet header)</div>
            <div className="space-y-1.5">
              {LEAD_FIELDS.map((f) => (
                <div key={f} className="flex items-center gap-2">
                  <span className="text-xs font-semibold w-20 capitalize">{f}</span>
                  <input
                    value={columnMap[f] || ""}
                    onChange={(e) => updateCol(f, e.target.value)}
                    placeholder={f === "phone" ? "Required — e.g. Phone" : `Sheet header for ${f}`}
                    className="flex-1 h-9 px-2 border border-border rounded-sm text-sm font-mono"
                  />
                  {columnMap[f] && (
                    <button onClick={() => removeCol(f)} className="text-xs text-muted-foreground hover:text-[hsl(var(--critical))] px-1">×</button>
                  )}
                </div>
              ))}
            </div>
            <div className="text-[11px] text-muted-foreground mt-2">
              At least <code>phone</code> must be mapped (the dedupe key). Header matching is case- and whitespace-insensitive ("Mobile_Number" matches "mobile number").
            </div>
          </div>
        </div>

        <div className="flex gap-2 mt-6 pt-4 border-t border-border">
          <button
            onClick={save}
            disabled={saving}
            data-testid="gsheets-edit-save"
            className="flex-1 h-10 bg-primary text-primary-foreground text-sm font-semibold rounded-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
          >
            <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save changes"}
          </button>
          <button onClick={onClose} className="h-10 px-5 border border-border text-sm rounded-sm hover:bg-secondary">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function EditField({ label, children }) {
  return (
    <div>
      <div className="label-mono mb-1">{label}</div>
      {children}
    </div>
  );
}

function TestResultModal({ result, onClose }) {
  const { mapping, data } = result;
  const reasons = data.skipped_reasons || {};
  const warnings = data.warnings || {};
  const allOk = (data.preview || []).every((p) => p.lead);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-sm p-6 max-w-3xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="gsheets-test-modal">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="font-heading text-lg font-medium flex items-center gap-2"><Sparkles className="w-4 h-4 text-primary" /> Test mapping (dry-run)</div>
            <div className="text-xs text-muted-foreground mt-1 font-mono">{mapping.spreadsheet_name} · {mapping.sheet_name}</div>
          </div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>

        <div className="grid grid-cols-3 gap-2 mb-4">
          <Stat label="Total data rows" value={data.total_rows} />
          <Stat label="Sample previewed" value={(data.preview || []).length} />
          <Stat label="Detected headers" value={(data.headers || []).length} />
        </div>

        {data.missing_mapped_fields?.length > 0 && (
          <div className="mb-4 p-3 bg-[hsl(38_92%_50%)]/10 border border-[hsl(38_92%_50%)]/40 rounded-sm">
            <div className="text-xs font-semibold mb-1">Unmatched mapped fields</div>
            <div className="text-[11px] font-mono">{data.missing_mapped_fields.join(", ")}</div>
            <div className="text-[11px] text-muted-foreground mt-1">These fields point to headers that don't exist in the sheet — fix the mapping or rename the sheet header.</div>
          </div>
        )}

        <div className="mb-4">
          <div className="label-mono mb-2">Detected sheet headers</div>
          <div className="flex flex-wrap gap-1">
            {(data.headers || []).map((h, i) => (
              <span key={i} className="text-[11px] px-2 py-0.5 bg-secondary rounded-sm font-mono">{h}</span>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <div className="label-mono mb-2">Sample row preview</div>
          <div className="space-y-1.5">
            {(data.preview || []).map((row, i) => (
              <div key={i} className={`text-xs border-l-2 pl-3 py-1.5 ${row.lead ? "border-[hsl(var(--success))]" : "border-[hsl(var(--critical))]"}`}>
                <div className="flex justify-between">
                  <span className="font-semibold">Row {row.sheet_row}</span>
                  {row.lead ? (
                    <span className="text-[hsl(var(--success))]">would import</span>
                  ) : (
                    <span className="text-[hsl(var(--critical))] font-mono">skip · {row.skipped}</span>
                  )}
                </div>
                {row.lead && (
                  <div className="font-mono text-[11px] mt-1">
                    {row.lead.name || "—"} · {row.lead.phone} · {row.lead.city || "—"} · <span className="text-primary">{row.lead.date}</span>
                    {row.warning && <span className="ml-2 text-[hsl(38_92%_45%)]">⚠ {row.warning}</span>}
                  </div>
                )}
              </div>
            ))}
            {(data.preview || []).length === 0 && (
              <div className="text-xs text-muted-foreground p-3 border border-dashed border-border rounded-sm text-center">No preview rows.</div>
            )}
          </div>
        </div>

        {(Object.keys(reasons).length > 0 || Object.keys(warnings).length > 0) && (
          <div className="grid grid-cols-2 gap-3">
            {Object.keys(reasons).length > 0 && (
              <div>
                <div className="label-mono mb-1">Skip reasons (preview window)</div>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(reasons).map(([k, v]) => (
                    <span key={k} className="text-[11px] px-1.5 py-0.5 rounded-sm bg-secondary font-mono">{k}: <b>{v}</b></span>
                  ))}
                </div>
              </div>
            )}
            {Object.keys(warnings).length > 0 && (
              <div>
                <div className="label-mono mb-1">Warnings</div>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(warnings).map(([k, v]) => (
                    <span key={k} className="text-[11px] px-1.5 py-0.5 rounded-sm bg-[hsl(38_92%_50%)]/15 text-[hsl(38_92%_30%)] font-mono">{k}: <b>{v}</b></span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-4 pt-3 border-t border-border text-[11px] text-muted-foreground">
          {allOk
            ? "All previewed rows would import successfully. Run Sync to commit."
            : "Some rows would be skipped. Review the reasons above before running Sync."}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-border rounded-sm p-3 bg-secondary/20">
      <div className="label-mono">{label}</div>
      <div className="text-xl font-semibold tabular mt-0.5">{value ?? "—"}</div>
    </div>
  );
}
